import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { RiskLevel, Case, Team } from '../types';
import { AlertTriangle, CheckCircle, Activity, TrendingUp, Shield, Zap, Mic } from 'lucide-react';
import { UsageStats } from '../services/supabase';

// Sanitize text content to prevent XSS
const sanitizeText = (text: string): string => {
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

// Format date to JST (Japan Standard Time)
const formatDateTimeJST = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

// Generate date labels for the last 30 days (7 points)
const generateDateLabels = () => {
  const now = new Date();
  const labels: string[] = [];
  const dayOffsets = [29, 25, 20, 15, 10, 5, 0];

  dayOffsets.forEach((offset) => {
    const date = new Date(now);
    date.setDate(date.getDate() - offset);
    labels.push(`${date.getMonth() + 1}/${date.getDate()}`);
  });

  return labels;
};

// Default data when no cases available
const DEFAULT_ACTIVITY_DATA = generateDateLabels().map(label => ({
  name: label,
  posts: 0
}));

const DEFAULT_RISK_DATA = [
  { name: '低', value: 0, color: '#22c55e' },
  { name: '中', value: 0, color: '#eab308' },
  { name: '高', value: 0, color: '#ef4444' },
];

/* アイコンの色。**クラス名を文字列から組み立てない。**

   以前は `text-${color.replace('bg-', '')}` としていた。Tailwind を
   ビルド時にコンパイルすると、走査はソースの文字列しか見ないため
   こうして作ったクラスは CSS に含まれず、色が消える。
   実際に使われている color は下の5通りだけなので、対応表で literal にする。 */
const ICON_COLOR: Record<string, string> = {
  'bg-blue-500': 'text-blue-500',
  'bg-red-500': 'text-red-500',
  'bg-orange-500': 'text-orange-500',
  'bg-amber-500': 'text-amber-500',
  'bg-teal-500': 'text-teal-500',
  'bg-slate-400': 'text-slate-400',
};

export const StatCard: React.FC<{ title: string; value: string | number; icon: any; trend?: string; color?: string; subtitle?: string }> = ({
  title, value, icon: Icon, trend, color = 'bg-blue-500', subtitle
}) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-start justify-between transition-transform hover:-translate-y-1">
    <div>
      <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
      <h3 className="text-3xl font-bold text-industrial-900">{value}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      {trend && <p className="text-xs text-emerald-600 flex items-center mt-2 font-medium"><TrendingUp size={12} className="mr-1"/> {trend}</p>}
    </div>
    <div className={`p-3 rounded-lg ${color} bg-opacity-10`}>
      <Icon className={ICON_COLOR[color] ?? 'text-blue-500'} size={24} />
    </div>
  </div>
);

export const ActivityChart: React.FC<{ cases?: Case[] }> = ({ cases = [] }) => {
  const activityData = useMemo(() => {
    const now = new Date();
    const data: { name: string; posts: number; weekStart: string; weekEnd: string }[] = [];

    // Create 5 weeks of data (weekly aggregation)
    for (let week = 4; week >= 0; week--) {
      // Calculate week start (Monday) and end (Sunday)
      const weekEnd = new Date(now);
      weekEnd.setHours(0, 0, 0, 0);
      weekEnd.setDate(weekEnd.getDate() - (week * 7));

      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekEnd.getDate() - 6);

      const weekStartStr = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
      const weekEndStr = `${weekEnd.getMonth() + 1}/${weekEnd.getDate()}`;
      const label = `${weekStartStr}〜`;

      const count = cases.filter(c => {
        const caseDate = new Date(c.createdAt);
        caseDate.setHours(0, 0, 0, 0);
        return caseDate >= weekStart && caseDate <= weekEnd;
      }).length;

      data.push({
        name: label,
        posts: count,
        weekStart: weekStartStr,
        weekEnd: weekEndStr
      });
    }

    return data;
  }, [cases]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-80">
      <h4 className="text-lg font-semibold text-industrial-900 mb-4">投稿推移（直近30日・週単位）</h4>
      <ResponsiveContainer width="100%" height="85%">
        <AreaChart data={activityData}>
          <defs>
            <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{fontSize: 11, fill: '#64748b'}}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{fontSize: 12, fill: '#64748b'}}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              padding: '8px 12px'
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]?.payload) {
                const data = payload[0].payload;
                return `${data.weekStart}〜${data.weekEnd}`;
              }
              return label;
            }}
            formatter={(value: number) => [`${value}件`, '投稿数']}
          />
          <Area
            type="monotone"
            dataKey="posts"
            stroke="#f97316"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorPosts)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const RiskPieChart: React.FC<{ cases?: Case[] }> = ({ cases = [] }) => {
  const riskData = useMemo(() => {
    if (cases.length === 0) return DEFAULT_RISK_DATA;

    const lowCount = cases.filter(c => c.riskLevel === RiskLevel.LOW).length;
    const mediumCount = cases.filter(c => c.riskLevel === RiskLevel.MEDIUM).length;
    const highCount = cases.filter(c => c.riskLevel === RiskLevel.HIGH).length;

    return [
      { name: '低', value: lowCount, color: '#22c55e' },
      { name: '中', value: mediumCount, color: '#eab308' },
      { name: '高', value: highCount, color: '#ef4444' },
    ];
  }, [cases]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-80">
      <h4 className="text-lg font-semibold text-industrial-900 mb-2">リスクレベル割合</h4>
      <ResponsiveContainer width="100%" height="90%">
        <PieChart>
          <Pie
            data={riskData}
            innerRadius={60}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
          >
            {riskData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex justify-center space-x-4 -mt-4">
        {riskData.map((item) => (
          <div key={item.name} className="flex items-center text-xs text-slate-600">
            <span className="w-2 h-2 rounded-full mr-1" style={{backgroundColor: item.color}}></span>
            {item.name} ({item.value})
          </div>
        ))}
      </div>
    </div>
  );
};

/* teams を受け取るのは現場名を出すため。ダッシュボードの「最新の報告」だけ
   現場が分からないと、一覧・QR とここで見え方が食い違う。 */
export const RecentCasesList: React.FC<{
  cases: Case[];
  teams?: Team[];
  onViewAll?: () => void;
}> = ({ cases, teams = [], onViewAll }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
    <div className="p-6 border-b border-slate-100 flex justify-between items-center">
      <h4 className="text-lg font-semibold text-industrial-900">最新の報告</h4>
      <button
        onClick={onViewAll}
        className="text-sm text-safety-600 hover:text-safety-700 font-medium"
      >
        すべて見る
      </button>
    </div>
    <div className="divide-y divide-slate-100">
      {cases.map((c) => (
        <div key={c.id} className="p-4 hover:bg-slate-50 transition-colors flex items-start space-x-4">
          <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
            c.riskLevel === RiskLevel.HIGH ? 'bg-red-500 shadow-red-200 shadow-lg' :
            c.riskLevel === RiskLevel.MEDIUM ? 'bg-yellow-500' : 'bg-green-500'
          }`} />
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start mb-1">
              <p className="text-sm font-medium text-industrial-900 truncate">{sanitizeText(c.title)}</p>
              <span className="text-xs text-slate-400 whitespace-nowrap ml-2">{formatDateTimeJST(c.createdAt)}</span>
            </div>
            <p className="text-xs text-slate-500 line-clamp-2 mb-2">{sanitizeText(c.body)}</p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                {c.category1}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                #{c.category2}
              </span>
              {c.teamId && teams.find((t) => t.id === c.teamId) && (
                <span
                  title={teams.find((t) => t.id === c.teamId)!.name}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200 max-w-[10rem] truncate"
                >
                  📍{teams.find((t) => t.id === c.teamId)!.name}
                </span>
              )}
              {c.imageUrl && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">
                  📷 写真あり
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const HeroDashboardMockup = () => (
  <div className="bg-slate-100 p-4 h-full w-full overflow-hidden flex flex-col font-sans">
    {/* Header - mimics ManagerLayout sidebar style */}
    <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-200">
       <div className="flex items-center space-x-2">
         <img src="/icon-512x512.png" alt="" className="h-7 w-7 rounded-lg" />
         <span className="font-bold text-sm text-slate-900 tracking-tight">安全ポスト＋</span>
       </div>
       <div className="flex space-x-3 text-[11px] text-slate-500 font-medium">
         <span className="text-safety-600 font-bold">ダッシュボード</span>
         <span>報告一覧</span>
         <span>QR管理</span>
       </div>
    </div>
    {/* Content */}
    <div className="space-y-3 flex-1 min-h-0 overflow-hidden">
       {/* KPI Row - 4 cards matching actual dashboard */}
       <div className="grid grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100">
             <p className="text-[10px] text-slate-500 font-medium">未対応High</p>
             <p className="text-xl font-bold text-red-500 mt-0.5">2</p>
             <p className="text-[9px] text-slate-400 mt-0.5">前月 -1</p>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100">
             <p className="text-[10px] text-slate-500 font-medium">未対応合計</p>
             <p className="text-xl font-bold text-orange-500 mt-0.5">8</p>
             <p className="text-[9px] text-slate-400 mt-0.5">前月 +3</p>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100">
             <p className="text-[10px] text-slate-500 font-medium">改善実施率</p>
             <p className="text-xl font-bold text-teal-600 mt-0.5">87%</p>
             <p className="text-[9px] text-emerald-500 mt-0.5">前月 +5pt</p>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100">
             <p className="text-[10px] text-slate-500 font-medium">滞留日数</p>
             <p className="text-xl font-bold text-slate-700 mt-0.5">3日</p>
             <p className="text-[9px] text-slate-400 mt-0.5">中央値</p>
          </div>
       </div>

       {/* Charts Row */}
       <div className="grid grid-cols-3 gap-3 flex-1 min-h-0">
          {/* Area Chart */}
          <div className="col-span-2 bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex flex-col">
             <p className="text-[11px] font-semibold text-slate-800 mb-2">投稿推移（直近30日・週単位）</p>
             <div className="flex-1 relative min-h-[80px]">
                {/* Simplified area chart visualization */}
                <svg className="w-full h-full" viewBox="0 0 200 60" preserveAspectRatio="none">
                   <defs>
                      <linearGradient id="heroGradient" x1="0" y1="0" x2="0" y2="1">
                         <stop offset="0%" stopColor="#f97316" stopOpacity="0.3"/>
                         <stop offset="100%" stopColor="#f97316" stopOpacity="0"/>
                      </linearGradient>
                   </defs>
                   <path d="M0,50 L40,45 L80,35 L120,40 L160,25 L200,20 L200,60 L0,60 Z" fill="url(#heroGradient)"/>
                   <path d="M0,50 L40,45 L80,35 L120,40 L160,25 L200,20" fill="none" stroke="#f97316" strokeWidth="2"/>
                   <circle cx="200" cy="20" r="3" fill="#f97316"/>
                </svg>
                {/* X-axis labels */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[8px] text-slate-400 px-1">
                   <span>12/1〜</span>
                   <span>12/8〜</span>
                   <span>12/15〜</span>
                </div>
             </div>
          </div>

          {/* Donut Chart */}
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex flex-col">
             <p className="text-[11px] font-semibold text-slate-800 mb-2">リスクレベル割合</p>
             <div className="flex-1 flex items-center justify-center">
                <svg width="70" height="70" viewBox="0 0 70 70">
                   <circle cx="35" cy="35" r="25" fill="none" stroke="#22c55e" strokeWidth="10" strokeDasharray="94 157" transform="rotate(-90 35 35)"/>
                   <circle cx="35" cy="35" r="25" fill="none" stroke="#eab308" strokeWidth="10" strokeDasharray="47 157" strokeDashoffset="-94" transform="rotate(-90 35 35)"/>
                   <circle cx="35" cy="35" r="25" fill="none" stroke="#ef4444" strokeWidth="10" strokeDasharray="16 157" strokeDashoffset="-141" transform="rotate(-90 35 35)"/>
                </svg>
             </div>
             <div className="flex justify-center gap-3 mt-1">
                <span className="flex items-center text-[9px] text-slate-600"><span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>低</span>
                <span className="flex items-center text-[9px] text-slate-600"><span className="w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>中</span>
                <span className="flex items-center text-[9px] text-slate-600"><span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>高</span>
             </div>
          </div>
       </div>

       {/* Recent Cases List */}
       <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 overflow-hidden">
          <div className="flex justify-between items-center mb-2">
             <p className="text-[11px] font-semibold text-slate-800">最新の報告</p>
             <span className="text-[10px] text-safety-600 font-medium">すべて見る</span>
          </div>
          <div className="space-y-1.5">
             {[
               {t: '足場の手すり緩み発見', risk: '高', cat: '設備', cat2: '足場'},
               {t: '通路に資材が放置されている', risk: '中', cat: '環境', cat2: '整理整頓'},
               {t: '重機旋回時の巻き込み注意', risk: '高', cat: '作業', cat2: '重機'},
             ].map((item, i) => (
               <div key={i} className="flex items-start space-x-2 p-2 rounded-lg hover:bg-slate-50 transition cursor-pointer">
                  <div className={`w-1.5 h-1.5 mt-1.5 rounded-full flex-shrink-0 ${item.risk === '高' ? 'bg-red-500' : item.risk === '中' ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                  <div className="min-w-0 flex-1">
                     <p className="text-[10px] font-medium text-slate-800 truncate">{item.t}</p>
                     <div className="flex gap-1 mt-0.5">
                        <span className="text-[8px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{item.cat}</span>
                        <span className="text-[8px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">#{item.cat2}</span>
                     </div>
                  </div>
               </div>
             ))}
          </div>
       </div>
    </div>
  </div>
);

// Usage Stats Card for Dashboard
interface UsageBarProps {
  used: number;
  limit: number;
  label: string;
  icon: React.ReactNode;
}

const UsageBar: React.FC<UsageBarProps> = ({ used, limit, label, icon }) => {
  const percentage = Math.min((used / limit) * 100, 100);
  const isWarning = percentage >= 80;
  const isExceeded = percentage >= 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>
        <span className={`text-sm font-bold ${isExceeded ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-600'}`}>
          {used} / {limit}
        </span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all ${
            isExceeded ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-safety-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {isExceeded && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle size={12} />
          上限に達しました
        </p>
      )}
      {isWarning && !isExceeded && (
        <p className="text-xs text-amber-600">残り{limit - used}回</p>
      )}
    </div>
  );
};

export const UsageStatsCard: React.FC<{
  usageStats: UsageStats | null;
  isLoading?: boolean;
  planName?: string;
}> = ({ usageStats, isLoading = false, planName = 'free' }) => {
  /* 表示名は料金ページ・設定画面と揃える（light = チーム）。
     ここだけ「ライト」だと、同じプランが画面ごとに別の名前に見える。 */
  const planDisplayName = {
    free: 'フリー',
    light: 'チーム',
    standard: 'スタンダード',
    enterprise: 'エンタープライズ'
  }[planName] || 'フリー';

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-slate-200 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-4 bg-slate-200 rounded"></div>
            <div className="h-2.5 bg-slate-200 rounded"></div>
            <div className="h-4 bg-slate-200 rounded"></div>
            <div className="h-2.5 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!usageStats) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
        <h4 className="text-lg font-semibold text-industrial-900 mb-4">今月の使用状況</h4>
        <p className="text-sm text-slate-500">使用状況を読み込めませんでした</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-base font-semibold text-industrial-900">今月の使用状況</h4>
        <span className="px-2 py-0.5 text-xs font-medium bg-safety-100 text-safety-700 rounded-full">
          {planDisplayName}プラン
        </span>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <UsageBar
          used={usageStats.aiAnalysis.used}
          limit={usageStats.aiAnalysis.limit}
          label="AI分析"
          icon={<Zap size={16} className="text-safety-500" />}
        />
        <UsageBar
          used={usageStats.audioTranscription.used}
          limit={usageStats.audioTranscription.limit}
          label="音声入力"
          icon={<Mic size={16} className="text-blue-500" />}
        />
      </div>
    </div>
  );
};